import { Box } from '@chakra-ui/react'
import { useEffect } from 'react'

import isTypedObjectKey from '@/common/utils/typescript/isTypedObjectKey'

import SeriesQ from '@/queries/SeriesQ'
import UserQ from '@/queries/UserQ'
import WorldQ from '@/queries/WorldQ'
import { useUser } from '@/client/context/user'
import type { Query } from '@/client/query'
import { trackEvent } from '@/client/services/analytics'
import { useSeriesRegistrationWizardSteps } from '@/client/services/formInputs'
import { PROGRAM_WORLD_TO_PROGRAM_SLUG } from '@/client/services/programs/constants'
import isSATBootcampTemplateSlug from '@/client/services/satBootcamp/isSATBootcampTemplateSlug'
import { SERIES_RESERVATIONS_MAX } from '@/client/services/series/constants'
import { getSeriesUrl } from '@/client/services/url'

import Alert from '@/components/UI/Alert'
import FormWizardV2 from '@/components/UI/FormWizardV2/FormWizardV2'
import ModalV3 from '@/components/UI/Modal/ModalV3'

export default function SeriesRegistrationModal({
  open,
  onClose,
  series,
}: {
  open: boolean
  onClose: () => void
  series: Query
}): JSX.Element {
  const user = useUser()

  const seriesId = series(SeriesQ.id) ?? 0
  const seriesQuestionnaire = series(SeriesQ.questionnaire)
  const attendancePolicy = `${series(SeriesQ.attendance_policy)}`
  const researchGroup = series(SeriesQ.getResearchGroup())
  const hasRequiredAttendanceSessions = series(SeriesQ.hasRequiredAttendanceSessions()) ?? false
  const countUserReservationsInSeries =
    series(SeriesQ.countActiveReservations({ reserverUserId: user.id })) ?? 0
  // ^ We do not want to provide an infinite number of reservations, so if you previously
  // invited friends and then cancelled your reservations.

  const isRequiredPhoneNumberSeries = series(SeriesQ.isRequiredPhoneNumberSeries())

  const seriesTemplateSlug = series(SeriesQ.getSeriesTemplateSlug())
  const isSATBootcampCourse = isSATBootcampTemplateSlug(seriesTemplateSlug)

  const maxLearners = series(SeriesQ.max_learners) ?? 0
  const countRegistrations = series(SeriesQ.countRegistrations()) ?? 0
  const countActiveReservations = series(SeriesQ.countActiveReservations()) ?? 0
  const spotsAvailableToReserve = Math.min(
    SERIES_RESERVATIONS_MAX,
    Math.max(0, maxLearners - countRegistrations - countActiveReservations - 1)
    // ^ -1 because current user will also take one spot
  )

  const tutor = series.one(SeriesQ.getUser())
  const tutorName = tutor(UserQ.display_name) ?? ''
  const tutorAvatarUrl = tutor(UserQ.avatar_url) ?? ''

  const world = series.one(SeriesQ.getWorld())
  const worldSlug = world(WorldQ.slug) ?? ''
  const programSlug = isTypedObjectKey(worldSlug, PROGRAM_WORLD_TO_PROGRAM_SLUG)
    ? PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug]
    : undefined

  const { content, handleSubmit, steps, blockedError } = useSeriesRegistrationWizardSteps({
    seriesId,
    attendancePolicy,
    seriesQuestionnaire,
    hasRequiredAttendanceSessions,
    programSlug,
    templateSlug: seriesTemplateSlug,
    tutorInfo: { name: tutorName, avatarUrl: tutorAvatarUrl },
    spotsAvailableToReserve,
    countUserReservationsInSeries,
    requirePhoneNumber: isRequiredPhoneNumberSeries ?? false,
    onClose,
    redirectUrl: getSeriesUrl(seriesId),
    HeaderComponent: () => null,
    source: 'series-page',
    researchGroup: researchGroup ?? null,
  })

  useEffect(() => {
    trackEvent({
      category: 'SAT Bootcamp - Registration',
      action: 'seen - SeriesRegistrationModal',
    })
  }, [])

  return (
    <>
      {content}
      <ModalV3
        disableClickOutside
        open={open}
        onClose={onClose}
        size="3xl"
        title={() => <Box height="4px" />}
        body={() => (
          <Box>
            {blockedError == null ? null : (
              <Alert variant="error" mb="24px">
                {blockedError}
              </Alert>
            )}
            <FormWizardV2
              enableAnalyticsTracking
              name={
                isSATBootcampCourse ? 'SATSeriesRegistrationWizard' : 'SeriesRegistrationWizard'
              }
              steps={steps}
              onSubmit={handleSubmit}
              checklistType="horizontal-stepper"
              maxWidth="100%"
              excludeNavbarPadding
            />
          </Box>
        )}
      />
    </>
  )
}
